<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'tags' }"
          >Tags</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="my-6">
        <v-form ref="form" class="tag-form">
          <label class="text-field-label">Tag name</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.tag_name"
            :error-messages="form.$getError('tag_name')"
            :loading="loading"
          ></v-text-field>

          <label class="text-field-label">Goal description</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.tag_description"
            :error-messages="form.$getError('tag_description')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="tag ? updateTagDetails() : addTag()"
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn color="red" text @click="removeTag" :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'

import Form from '@/utils/form'

export default {
  name: 'TagDetails',

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
      },
      loading: false,
      deleteDialog: false,
      form: new Form({
        tag_name: '',
        tag_description: '',
      }),

      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showModal: false,
    }
  },

  computed: {
    ...mapState({
      tag: (state) => state.tag.tagDetails,
      authUser: (state) => state.auth.user,
    }),
    displayName() {
      return this.form.tag_name
    },
  },

  methods: {
    ...mapActions({
      getTagDetails: 'tag/getTagDetails',
      createTag: 'tag/createTag',
      updateTag: 'tag/updateTag',
      deleteTag: 'tag/deleteTag',
    }),

    ...mapMutations({
      clearTagDetails: 'tag/clearTagDetails',
    }),

    async getTag() {
      this.loading = true
      await this.getTagDetails(this.$route.params.id)
      this.form = new Form(this.tag)
      this.loading = false
    },

    async updateTagDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateTag(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Tag details successfully updated!', 'success')
          this.$router.replace({ name: 'tags' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addTag() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.createTag(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Tag successfully added!', 'success')
          this.$router.replace({ name: 'tags' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeTag() {
      this.form.$busy = true
      this.deleteTag(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Tag deleted successfully!')
          this.$router.replace('/tags')
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The tag you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },

  created() {
    if (this.$router.currentRoute.name === 'tag.details') {
      this.getTag()
    }
  },

  destroyed() {
    this.clearTagDetails()
  },

  watch: {
    $route() {
      this.getTag()
    },
  },
}
</script>

<style lang="scss" scoped>
.tag-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
